import React, { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';

// Types
import { FormData, ProductType, ConsentState, SingleAnswerSlide, FormInputSlide } from './types';
import { DEFAULT_CONSENT_STATE } from '../shared/consent/constants';

// Hooks
import { useSliderNavigation, useZipCodeCheck, useSavingsCalculator } from './hooks';

// Components
import ProgressBar from './components/ProgressBar';
import SlideHeader from './components/SlideHeader';
import ChoiceGrid from './components/ChoiceGrid';
import FormInput from './components/FormInput';
import PartnerConsentSlide from './components/PartnerConsentSlide';
import SuccessSlide from './components/SuccessSlide';

// Utils
import { submitLead, updateConsent } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
} from './utils/tracking';
import { getUTMParams, splitFullName } from './utils/helpers';

/**
 * SliderPrivacy - Componente principale del wizard di registrazione
 * SHORT VERSION: CAP slide comes first, removed install_timeline and decision_stage
 * Shows combined PV+HP bonus count in CAP slide since product type is selected after
 */
const SliderPrivacy: React.FC = () => {
  // Form data state
  const [formData, setFormData] = useState<FormData>({});
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  // Lead state
  const [leadUid, setLeadUid] = useState<string>('');
  const [mongoId, setMongoId] = useState<string>('');

  // Consent state
  const [consent, setConsent] = useState<ConsentState>(DEFAULT_CONSENT_STATE);

  // Scelta esplicita sul consenso marketing: null finché l'utente non risponde
  const [marketingChoice, setMarketingChoice] = useState<boolean | null>(null);

  // Navigation hook
  const {
    currentSlide,
    progress,
    goNext,
    goPrev,
    goToSlideById,
    canGoPrev,
  } = useSliderNavigation(formData);

  // ZIP check hook with feedback
  const { zipFlag, isValid: isCapValid, capFeedback, checkZip } = useZipCodeCheck();

  // Savings calculator hook - with zipCode for FVG support
  const { getBenefitText, getNextButtonText, getBonusCountText } = useSavingsCalculator(
    formData.product_type as ProductType,
    zipFlag,
    formData.zip
  );

  // Track initial source
  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');
  }, []);

  // Update form data with a new value
  const updateFormData = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Handle single answer selection
  const handleAnswerSelect = useCallback(
    (slide: SingleAnswerSlide, value: string) => {
      updateFormData(slide.apiFieldName, value);
      trackSlideAnswer(slide.id, slide.title || '', value);

      if (slide.autoAdvance) {
        setTimeout(() => goNext(), 200);
      }
    },
    [updateFormData, goNext]
  );

  // Handle form input change
  const handleFormInputChange = useCallback(
    (name: string, value: string) => {
      setFormValues((prev) => ({ ...prev, [name]: value }));

      // Special handling for ZIP code
      if (name === 'zip') {
        checkZip(value, formData.product_type);
        // Also update formData immediately for FVG check
        updateFormData('zip', value);
      }
    },
    [checkZip, updateFormData, formData.product_type]
  );

  const handleMarketingConsentChange = useCallback((value: boolean) => {
    setMarketingChoice(value);
    setConsent((prev) => ({ ...prev, enpalMarketingConsent: value }));
  }, []);

  // Submit lead to API (triggered from phone CTA)
  const handleLeadSubmit = useCallback(async () => {
    goToSlideById('loading');

    const uid = uuidv4();
    setLeadUid(uid);

    // Merge form values with form data
    const finalFormData: FormData = {
      ...formData,
      ...formValues,
    };

    try {
      const id = await submitLead(finalFormData, uid, consent);

      if (id) {
        setMongoId(id);
        trackSliderSuccess();

        // Track lead with hashed email
        if (finalFormData.email) {
          const { firstName, lastName } = splitFullName(finalFormData.name || '');
          setTimeout(() => {
            trackLeadCreated(
              finalFormData.email!,
              finalFormData.phone || '',
              finalFormData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }

        goToSlideById('partner_consent');
      } else {
        trackSliderFailure();
        goToSlideById('error');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
      goToSlideById('error');
    }
  }, [formData, formValues, consent, goToSlideById]);

  // Handle form submission for input slides
  const handleFormSubmit = useCallback(
    async (slide: FormInputSlide) => {
      // Copy form values to form data
      Object.entries(formValues).forEach(([key, value]) => {
        updateFormData(key, value);
      });

      trackSlideAnswer(slide.id, slide.title || '', 'continue_button');

      // Phone CTA submits the lead, then continues to partner consent
      if (slide.id === 'phone_form') {
        await handleLeadSubmit();
      } else {
        goNext();
      }
    },
    [formValues, updateFormData, goNext, handleLeadSubmit]
  );

  // Partner consent: keep checkbox state, update consent, go to thank you
  const handlePartnerContinue = useCallback(async () => {
    trackSlideAnswer('partner_consent', 'partner_consent', 'continue');
    if (leadUid) {
      await updateConsent(leadUid, consent);
    }
    goToSlideById('success');
  }, [leadUid, consent, goToSlideById]);

  // Partner consent: force partner opts-out, update consent, go to thank you
  const handlePartnerContinueWithoutAccepting = useCallback(async () => {
    const declinedConsent: ConsentState = {
      ...consent,
      thirdPartyMarketing: false,
      thirdPartyProfiling: false,
    };
    setConsent(declinedConsent);
    trackSlideAnswer('partner_consent', 'partner_consent', 'continue_without_accepting');
    if (leadUid) {
      await updateConsent(leadUid, declinedConsent);
    }
    goToSlideById('success');
  }, [leadUid, consent, goToSlideById]);

  // Handle error retry
  const handleRetry = useCallback(() => {
    goToSlideById('location_form');
  }, [goToSlideById]);

  // Get dynamic button text for form slides
  const getSubmitLabel = (slideId: string): string => {
    if (slideId === 'phone_form') {
      return 'Scopri i rimanenti bonus';
    }
    if (slideId === 'location_form') {
      return 'Scopri dettaglio Bonus';
    }
    if (slideId === 'name_form' || slideId === 'email_form') {
      return getNextButtonText(slideId);
    }
    return 'Avanti';
  };

  // Render current slide content
  const renderSlideContent = () => {
    if (!currentSlide) return null;

    switch (currentSlide.type) {
      case 'SINGLE_ANSWER': {
        const slide = currentSlide as SingleAnswerSlide;
        const benefitText = getBenefitText(slide.id);
        const bonusCountText = getBonusCountText(slide.id);

        // Determine column count based on slide
        const columns: 1 | 2 | 3 = (slide.id === 'install_timeline' || slide.id === 'decision_stage') ? 2 : 3;

        // Determine layout based on slide
        const layout = slide.id === 'customer_type' ? 'horizontal' : 'vertical';

        return (
          <>
            {/* Header con benefici - solo per slide con header */}
            {slide.header && (
              <SlideHeader
                savingsSubText={bonusCountText || slide.header.savingsSubText}
                savingsText={benefitText || slide.header.savingsText}
                zipCode={formData.zip}
              />
            )}

            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{slide.title}</h3>

              <ChoiceGrid
                answers={slide.answers}
                selectedValue={formData[slide.apiFieldName]}
                onSelect={(value) => handleAnswerSelect(slide, value)}
                columns={columns}
                layout={layout}
              />

              {!slide.autoAdvance && (
                <div className={styles.buttonGroup}>
                  {canGoPrev && (
                    <button type="button" className={styles.btnPrev} onClick={goPrev}>
                      Indietro
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.btnNext}
                    onClick={goNext}
                    disabled={!formData[slide.apiFieldName]}
                  >
                    Avanti
                  </button>
                </div>
              )}
            </div>
          </>
        );
      }

      case 'FORM_INPUT': {
        const slide = currentSlide as FormInputSlide;
        const benefitText = getBenefitText(slide.id);
        const bonusCountText = getBonusCountText(slide.id);
        const isPhoneSlide = slide.id === 'phone_form';
        const isLocationSlide = slide.id === 'location_form';

        return (
          <>
            {slide.header && (
              <SlideHeader
                savingsSubText={bonusCountText || slide.header.savingsSubText}
                savingsText={benefitText || slide.header.savingsText}
                zipCode={formData.zip}
              />
            )}

            <div className={styles.slideBody}>
              {slide.title && (
                <h3 className={styles.wizardTitle}>
                  {isLocationSlide ? (
                    <>Inserisci il <span className={styles.highlightText}>TUO CAP</span> e scopri Incentivi</>
                  ) : (
                    slide.title
                  )}
                </h3>
              )}

              <FormInput
                inputs={slide.inputs}
                values={formValues}
                onChange={handleFormInputChange}
                onSubmit={() => handleFormSubmit(slide)}
                submitLabel={getSubmitLabel(slide.id)}
                showBackButton={canGoPrev && !isPhoneSlide}
                onBack={goPrev}
                isPhoneSlide={isPhoneSlide}
                isLocationSlide={isLocationSlide}
                capFeedback={capFeedback}
                isCapValid={isCapValid}
                marketingConsent={marketingChoice}
                onMarketingConsentChange={
                  isPhoneSlide ? handleMarketingConsentChange : undefined
                }
              />
            </div>
          </>
        );
      }

      case 'INFO': {
        if (currentSlide.id === 'partner_consent') {
          return (
            <PartnerConsentSlide
              consent={consent}
              onConsentChange={setConsent}
              onContinue={handlePartnerContinue}
              onContinueWithoutAccepting={handlePartnerContinueWithoutAccepting}
            />
          );
        }

        if (currentSlide.id === 'loading') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Attendere prego...</p>
              <img
                src="https://i.gifer.com/origin/34/34338d26023e5515f6cc8969aa027bca.gif"
                alt="Loading..."
                className={styles.loadingImage}
              />
            </div>
          );
        }

        if (currentSlide.id === 'success') {
          return (
            <SuccessSlide
              productType={(formData.product_type || 'Photovoltaic') as ProductType}
              zipFlag={zipFlag}
              zipCode={formData.zip}
              mongoId={mongoId}
              phone={formData.phone || formValues.phone}
            />
          );
        }

        if (currentSlide.id === 'error') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Si è verificato un errore...</p>
              <button
                type="button"
                className={styles.btnNext}
                onClick={handleRetry}
                style={{ marginTop: '1rem', maxWidth: '200px' }}
              >
                Riprova
              </button>
            </div>
          );
        }

        return (
          <div className={styles.slideBody}>
            {currentSlide.title && <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>}
            {currentSlide.html && (
              <div dangerouslySetInnerHTML={{ __html: currentSlide.html }} />
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <section className={styles.sliderSection}>
      <div className={styles.sliderContainer}>
        <div className={styles.sliderWrapper}>
          <ProgressBar progress={progress} />

          <div className={styles.wizSlide}>
            {renderSlideContent()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SliderPrivacy;
